/**
 * @file Provides authentication functionality via a React context provider.
 */

import { createContext, useContext, useEffect, useMemo } from 'react'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { validateSchema } from '@fe-toolkit/axios-api-toolkit'
import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query'

import { TMemberSchemaType } from '@/api'
import { memberSchema } from '@/api/schemas/member.schemas'
import { PublicApiEndpointURLs } from '@/constants'
import MainLayout from '@/layouts/Main/Main'
import FullPageLoader from '@/components/molecules/FullPageLoader'

import { useError } from '../ErrorContext'

/**
 * @property {boolean} isAuthenticated - Flag to indicate whether the user is authenticated or not.
 * @property {boolean} isCheckingMemberAuth - Flag to indicate if the authentication check is ongoing.
 * @property {boolean} isCheckingMemberAuthError - Flag to indicate if there was an error during the authentication check.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsAuthenticated - Function to set the isAuthenticated state.
 * @property {function} handleLogout - Function to handle user logout.
 * @property {TMemberSchemaType | undefined} member - The authenticated member data.
 */

type TMemberContextProps = UseQueryResult<AxiosResponse<TMemberSchemaType>, AxiosError> & {
  hasMember: boolean
}

/**
 * The MemberContext provides the authentication-related data and functionalities.
 */
const MemberContext = createContext<TMemberContextProps>({} as TMemberContextProps)

/**
 * Default Axios options for the API requests.
 */
const axiosDefaultOptions: AxiosRequestConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
}

type TMemberProviderProps = {
  children: React.ReactNode
  privateRoutes?: string[]
  authenticationPathUrl: string
  checkMemberAuthEndpointUrl?: string
  redirectPathUrl?: string
  axiosOptions?: AxiosRequestConfig
  reactQueryOptions?: UseQueryOptions<AxiosResponse<TMemberSchemaType>, AxiosError>
  onSettled?: (data: AxiosResponse<TMemberSchemaType> | undefined, error: AxiosError | null) => void
  onSuccess?: (data: AxiosResponse<TMemberSchemaType>) => void
  onError?: (error: AxiosError) => void
  onLogout?: () => void
}

/**
 * MemberProvider component to provide authentication functionalities.
 * @param {TMemberProviderProps} props - The props for the MemberProvider component.
 * @property {React.ReactNode} children - The children components to wrap with this provider.
 * @property {string | undefined} checkMemberAuthEndpointUrl - The API endpoint URL to check for member authentication.
 * @property {AxiosRequestConfig | undefined} axiosOptions - Optional Axios configuration.
 * @property {UseQueryOptions<AxiosResponse<TMemberSchemaType>, AxiosError> | undefined} reactQueryOptions - Optional react-query configurations.
 * @property {(data: AxiosResponse<TMemberSchemaType> | undefined, error: AxiosError | null) => void | undefined} onSettled - Callback function to be called when the query is settled.
 * @property {(data: AxiosResponse<TMemberSchemaType>) => void | undefined} onSuccess - Callback function to be called when the query is successful.
 * @property {(error: AxiosError) => void | undefined} onError - Callback function to be called when the query has an error.
 */
export const MemberProvider = ({
  children,
  checkMemberAuthEndpointUrl = PublicApiEndpointURLs.ME,
  axiosOptions,
  reactQueryOptions,
  onSettled,
  onSuccess,
  onError,
}: TMemberProviderProps) => {
  const { setErrorCode, setErrorMessage } = useError()

  const query = useQuery<AxiosResponse<TMemberSchemaType>, AxiosError>({
    queryKey: ['member'],
    queryFn: async () =>
      axios.get(checkMemberAuthEndpointUrl, {
        ...axiosDefaultOptions,
        ...axiosOptions,
      }),
    ...reactQueryOptions,
    retry: false,
  })

  useEffect(() => {
    // callback on success
    if (query.isSuccess && query.data) {
      if (query.data.config && query.data.data) {
        const validate = async () => {
          await validateSchema({
            schema: memberSchema.strip(),
            data: query.data.data,
            context: `API Response for ${query.data.config.method} ${query.data.config.url}`,
          })
          onSuccess?.(query.data)
        }

        validate()
      } else {
        setErrorMessage('Invalid API response')
        setErrorCode(500)
      }
    }
  }, [onSuccess, query.data, query.isSuccess, setErrorCode, setErrorMessage])

  useEffect(() => {
    // callback on error
    if (query.isError) {
      onError?.(query.error)
    }
  }, [onError, query.error, query.isError])

  // on settled
  useEffect(() => {
    if (query.isFetched) {
      onSettled?.(query.data, query.error)
    }
  }, [onSettled, query.data, query.error, query.isFetched])

  const contextValue = useMemo<TMemberContextProps>(() => {
    return {
      ...query,
      hasMember: !!query.data?.data,
    }
  }, [query])

  // Block rendering of everything below this provider until the initial auth check has
  // settled - regardless of route, so no page can render authenticated content before we
  // know whether the member is actually logged in.
  return (
    <MemberContext.Provider value={contextValue}>
      {query.isLoading ? (
        <MainLayout>
          <FullPageLoader />
        </MainLayout>
      ) : (
        children
      )}
    </MemberContext.Provider>
  )
}

/**
 * Custom hook to access the MemberContext.
 *
 * @returns {TMemberContextProps} The Auth context values.
 */
export const useMemberContext = () => {
  const context = useContext(MemberContext)

  if (context === undefined) {
    throw new Error('useMemberContext must be used within MemberProvider')
  }

  return context
}
